import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Clock, TrendingUp, Lock, Zap } from "lucide-react";

interface TipCardProps {
  tip: {
    id: string;
    sport: string;
    league: string;
    match: string;
    tipType: string;
    odds: number;
    confidence: number;
    price: number;
    currency: string;
    isPremium: boolean;
    isAI?: boolean;
    timeLeft: string;
    tipsterName: string;
  };
}

export function TipCard({ tip }: TipCardProps) {
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return "text-profit";
    if (confidence >= 60) return "text-premium";
    return "text-muted-foreground";
  };

  const getConfidenceBadgeVariant = (confidence: number) => {
    if (confidence >= 80) return "bg-profit/10 text-profit border-profit/20";
    if (confidence >= 60) return "bg-premium/10 text-premium border-premium/20";
    return "bg-muted text-muted-foreground border-border";
  };

  return (
    <Card className="bg-gradient-card border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-card backdrop-blur-sm">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                {tip.sport}
              </Badge>
              {tip.isAI && (
                <Badge variant="outline" className="bg-secondary/50 text-secondary-foreground border-secondary">
                  <Zap className="h-3 w-3 mr-1" />
                  AI
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-foreground text-sm">{tip.league}</h3>
            <p className="text-muted-foreground text-xs">{tip.match}</p>
          </div>

          <div className="text-right">
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
              <Clock className="h-3 w-3" />
              <span>{tip.timeLeft}</span>
            </div>
            <div className="text-sm font-medium text-foreground">
              @{tip.odds.toFixed(2)}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Tip Details */}
        <div className="bg-background/50 rounded-lg p-3 mb-4 border border-border/30">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Tip</span>
            <span className="text-xs text-muted-foreground">by {tip.tipsterName}</span>
          </div>
          
          {tip.isPremium ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Lock className="h-4 w-4" />
              <span className="text-sm">Premium tip - purchase to unlock</span>
            </div>
          ) : (
            <div className="text-sm font-medium text-foreground">
              {tip.tipType}
            </div>
          )}
        </div>

        {/* Confidence & Price */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="text-xs text-muted-foreground">Confidence</span>
            <Badge className={`text-xs ${getConfidenceBadgeVariant(tip.confidence)}`}>
              {tip.confidence}%
            </Badge>
          </div>

          <div className="text-right">
            <div className="text-lg font-bold text-foreground">
              {tip.price === 0 ? 'FREE' : `${tip.price} ${tip.currency}`}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <Button 
          className="w-full" 
          variant={tip.isPremium ? "premium" : tip.price === 0 ? "betting" : "profit"}
        >
          {tip.price === 0 ? 'View Tip' : tip.isPremium ? 'Unlock Premium' : `Buy for ${tip.price} ${tip.currency}`}
        </Button>
      </CardContent>
    </Card>
  );
}